import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { KeyRound, Copy, Check } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useConnectionInfo, useDeviceActions } from '../hooks/useBiometricAdmin';
import type { BiometricDevice } from '../biometricTypes';

interface Props {
  device?: BiometricDevice | null;
  onClose: () => void;
}

/** Add or edit a push device, and show the exact keypad steps for the machine. */
export function DeviceRegisterModal({ device, onClose }: Props) {
  // A machine that called in by itself is approved with the same form, which creates its record for this company.
  const isEdit = Boolean(device) && !device?.pending_approval;
  const { register, update, generateKey } = useDeviceActions();

  const [form, setForm] = useState({
    serial_number: device?.serial_number ?? '',
    name: device?.name ?? '',
    location: device?.location ?? '',
    auth_key: device?.auth_key ?? '',
    timezone_offset: device?.timezone_offset ?? '5.5',
    delay_seconds: String(device?.delay_seconds ?? 10),
    ack_style: device?.ack_style ?? 'OK: n',
    is_active: device?.pending_approval ? true : device?.is_active ?? true,
    // Reading the machine over the office network, as well as (or instead of) it pushing to the server.
    lan_poll: device?.lan_poll ?? false,
    ip_address: device?.ip_address ?? '',
    port: String(device?.port ?? 4370),
  });
  const { data: connection } = useConnectionInfo();
  const [copied, setCopied] = useState(false);

  const set = (key: keyof typeof form, value: string | boolean) =>
    setForm(prev => ({ ...prev, [key]: value }));

  // The machine calls the API server, not the address this page was opened from.
  const adms = (() => {
    try {
      const url = new URL(connection?.adms_url ?? '');
      return { host: url.hostname, port: url.port || (url.protocol === 'https:' ? '443' : '80') };
    } catch {
      return { host: 'your-server', port: '80' };
    }
  })();
  const serverHost = adms.host;
  const serverPort = adms.port;

  const submit = async () => {
    if (!form.serial_number.trim()) {
      toast.error('The serial number is required — it is how the device identifies itself.');
      return;
    }
    if (form.lan_poll && !form.ip_address.trim()) {
      toast.error('Reading over the network needs the machine IP address.');
      return;
    }
    const payload = {
      ...form,
      delay_seconds: Number(form.delay_seconds) || 10,
      port: Number(form.port) || 4370,
    };
    try {
      if (isEdit) {
        await update.mutateAsync({ sn: device!.serial_number, patch: payload });
        toast.success('Device updated.');
      } else {
        await register.mutateAsync(payload);
        toast.success(device?.pending_approval ? 'Device approved.' : 'Device registered and approved.');
      }
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const makeKey = async () => {
    try {
      set('auth_key', await generateKey.mutateAsync());
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const copySetup = async () => {
    const text = [
      'On the device keypad:',
      'Menu → Comm. → Cloud Server Setting (some models: ADMS)',
      '  Enable Domain Name : ON',
      `  Server Address     : ${serverHost}`,
      `  Server Port        : ${serverPort}`,
      '  Enable Proxy       : OFF',
      'Menu → Comm. → Network : DHCP ON (or static IP + gateway + DNS 8.8.8.8)',
      'Menu → System → Date & Time : set IST',
      'Save and let the device reboot.',
    ].join('\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? `Edit ${device!.serial_number}` : device ? `Approve ${device.serial_number}` : 'Add biometric device'}>
      <div className="space-y-4">
        <Field label="Serial number" hint="Menu → System Info → Device Info, or the sticker on the back.">
          <Input
            value={form.serial_number}
            onChange={e => set('serial_number', e.target.value)}
            disabled={Boolean(device)}
            placeholder="e.g. CJVX234960125"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name">
            <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Main Gate" />
          </Field>
          <Field label="Location">
            <Input value={form.location} onChange={e => set('location', e.target.value)} placeholder="Reception" />
          </Field>
        </div>

        <Field label="Auth key" hint="Required in the device's request. Leave blank to accept the serial number alone.">
          <div className="flex gap-2">
            <Input value={form.auth_key} onChange={e => set('auth_key', e.target.value)} placeholder="optional" />
            <Button variant="secondary" onClick={makeKey} type="button">
              <KeyRound className="mr-1 h-4 w-4" />
              Generate
            </Button>
          </div>
        </Field>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Timezone" hint="Hours (5.5) or minutes (330)">
            <Input value={form.timezone_offset} onChange={e => set('timezone_offset', e.target.value)} />
          </Field>
          <Field label="Poll interval (s)">
            <Input
              type="number"
              value={form.delay_seconds}
              onChange={e => set('delay_seconds', e.target.value)}
            />
          </Field>
          <Field label="Ack style" hint="Switch to bare OK if punches are re-sent">
            <select
              className="h-10 w-full rounded-md border border-outline-variant bg-surface px-3 text-sm text-on-surface"
              value={form.ack_style}
              onChange={e => set('ack_style', e.target.value)}
            >
              <option value="OK: n">OK: n (default)</option>
              <option value="OK">OK (legacy firmware)</option>
            </select>
          </Field>
        </div>

        <div className="space-y-3 rounded-md border border-outline-variant p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-on-surface">
            <input
              type="checkbox"
              checked={form.lan_poll}
              onChange={e => set('lan_poll', e.target.checked)}
              className="h-4 w-4 rounded border-outline-variant"
            />
            Also read this machine over the office network
          </label>
          <p className="text-xs text-on-surface-variant">
            The server connects to the machine itself (port 4370) and collects its punches and users every minute. Use this when the
            machine cannot push, or as a second path. The server must be able to reach the machine&apos;s address: a server in the
            cloud cannot see a private 192.168.x.x address.
          </p>
          {(form.lan_poll || form.ip_address) && (
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Field label="Machine IP address">
                  <Input value={form.ip_address} onChange={e => set('ip_address', e.target.value)} placeholder="192.168.1.201" />
                </Field>
              </div>
              <Field label="Port">
                <Input type="number" value={form.port} onChange={e => set('port', e.target.value)} />
              </Field>
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-on-surface">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={e => set('is_active', e.target.checked)}
            className="h-4 w-4 rounded border-outline-variant"
          />
          Approved — accept data from this device
        </label>

        <div className="rounded-md border border-outline-variant bg-surface-variant/40 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-on-surface">Set this on the device keypad</h4>
            <button
              type="button"
              onClick={copySetup}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy steps'}
            </button>
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap text-xs leading-relaxed text-on-surface-variant">
{`Menu → Comm. → Cloud Server Setting   (some models: ADMS)
  Enable Domain Name : ON
  Server Address     : ${serverHost}
  Server Port        : ${serverPort}
  Enable Proxy       : OFF

Menu → Comm. → Network
  DHCP ON, or static IP + gateway + DNS 8.8.8.8

Menu → System → Date & Time : set IST
Save — the device reboots and appears here within a minute.`}
          </pre>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={register.isPending || update.isPending}>
            {isEdit ? 'Save changes' : device ? 'Approve device' : 'Register device'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-on-surface">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-on-surface-variant">{hint}</p>}
    </div>
  );
}
